// src/pages/admin/AdminVideos.jsx
import { useEffect, useMemo, useState } from "react";
import "./Admin.css";
import { buildYouTubeThumbnail, extractYouTubeId } from "../../utils/videoData";

/* -------------------------------------------------------
   TEMPLATE
------------------------------------------------------- */
const emptyVideo = {
  id: null,
  title: "",
  status: "published",
  excerpt: "",
  category: "",
  duration: "",
  tags: [],
  publishedDate: "",
  youtubeUrl: "",
  thumbnailUrl: "",
  isFeatured: false,
};

/* -------------------------------------------------------
   COMPONENT
------------------------------------------------------- */
export default function AdminVideos() {
  /* ------------------------------
     STATE
  ------------------------------ */
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [mode, setMode] = useState("list"); // "list", "add", "edit"
  const [search, setSearch] = useState("");

  const [form, setForm] = useState(emptyVideo);
  const [, setEditingId] = useState(null);

  const [tagInput, setTagInput] = useState("");

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const youtubeId = useMemo(() => extractYouTubeId(form.youtubeUrl), [form.youtubeUrl]);
  const autoThumb = youtubeId ? buildYouTubeThumbnail(youtubeId) : "";

  /* ------------------------------
     LOAD VIDEOS
  ------------------------------ */
  useEffect(() => {
    async function load() {
      try {
        const res = await fetch("/.netlify/functions/get-videos");
        const json = await res.json();

        if (!res.ok || !json.ok) {
          setLoadError(json.error || "Failed to load videos.");
        } else {
          setVideos(json.videos || []);
        }
      } catch (err) {
        setLoadError("Network error while loading videos.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  /* ------------------------------
     FILTERED LIST
  ------------------------------ */
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return videos;

    return videos.filter((v) =>
      [v.title, v.tags?.join(" ")]
        .filter(Boolean)
        .some((s) => s.toLowerCase().includes(q))
    );
  }, [videos, search]);

  /* -------------------------------------------------------
     MODE HELPERS
  ------------------------------------------------------- */
  function startAdd() {
    setForm(emptyVideo);
    setEditingId(null);
    setTagInput("");
    setSaveError(null);
    setMode("add");
  }

  function startEdit(video) {
    setForm({
      ...emptyVideo,
      ...video,
      tags: Array.isArray(video.tags) ? video.tags : [],
    });
    setEditingId(video.id);
    setTagInput("");
    setSaveError(null);
    setMode("edit");
  }

  function cancelForm() {
    setMode("list");
    setForm(emptyVideo);
    setEditingId(null);
    setTagInput("");
    setSaveError(null);
  }

  /* -------------------------------------------------------
     FORM HANDLERS
  ------------------------------------------------------- */
  const handleField = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleCheckbox = (e) => {
    const { name, checked } = e.target;
    setForm((f) => ({ ...f, [name]: checked }));
  };

  /* ------------------------------
     TAGS
  ------------------------------ */
  function commitTagInput() {
    const raw = tagInput.trim();
    if (!raw) return;

    const newTags = raw
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    setForm((f) => {
      const existing = f.tags.map((t) => t.toLowerCase());
      const merged = [
        ...f.tags,
        ...newTags.filter((t) => !existing.includes(t.toLowerCase())),
      ];

      return { ...f, tags: merged };
    });

    setTagInput("");
  }

  const handleTagKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commitTagInput();
    }
    if (e.key === "Backspace" && !tagInput && form.tags.length > 0) {
      e.preventDefault();
      setForm((f) => ({ ...f, tags: f.tags.slice(0, -1) }));
    }
  };

  const removeTag = (tag) => {
    setForm((f) => ({
      ...f,
      tags: f.tags.filter((t) => t !== tag),
    }));
  };

  /* -------------------------------------------------------
     SAVE VIDEO
  ------------------------------------------------------- */
  async function saveVideo(e) {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);

    try {
      const res = await fetch("/.netlify/functions/save-video", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const json = await res.json();

      if (!res.ok || !json.ok) {
        setSaveError(json.error || "Failed to save video.");
        setSaving(false);
        return;
      }

      const saved = json.video;

      setVideos((list) => {
        const idx = list.findIndex((v) => v.id === saved.id);
        if (idx >= 0) {
          const updated = [...list];
          updated[idx] = saved;
          return updated;
        }
        return [...list, saved];
      });

      cancelForm();
    } catch (err) {
      setSaveError("Network error while saving.");
    } finally {
      setSaving(false);
    }
  }

  /* -------------------------------------------------------
     DELETE VIDEO
  ------------------------------------------------------- */
  async function confirmDelete() {
    if (!deleteTarget) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      const res = await fetch("/.netlify/functions/delete-video", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: deleteTarget.id }),
      });
      const json = await res.json();

      if (!res.ok || !json.ok) {
        setDeleteError(json.error || "Failed to delete video.");
        setDeleting(false);
        return;
      }

      setVideos((arr) => arr.filter((v) => v.id !== deleteTarget.id));
      setDeleteTarget(null);
      setDeleting(false);
    } catch (err) {
      setDeleteError("Network error while deleting.");
      setDeleting(false);
    }
  }

  /* #######################################################
     RENDERING
  ####################################################### */

  /* -------------------------------------------------------
     LOADING / ERROR STATES
  ------------------------------------------------------- */
  if (loading && mode === "list") {
    return <p>Loading videos…</p>;
  }

  if (loadError && mode === "list") {
    return <p className="av-error">{loadError}</p>;
  }

  /* -------------------------------------------------------
     FORM MODE (ADD / EDIT)
  ------------------------------------------------------- */
  if (mode === "add" || mode === "edit") {
    return (
      <div className="admin-events">
        <h1>{mode === "add" ? "Add New Video" : "Edit Video"}</h1>

        <form className="av-form" onSubmit={saveVideo}>
          {/* BASIC INFO */}
          <section className="av-section">
            <h2 className="av-section-title">Basic Info</h2>

            <div className="av-row">
              <div className="av-field">
                <label>Title</label>
                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleField}
                  placeholder="Optional (auto-filled from YouTube if blank)"
                />
              </div>
            </div>

            <div className="av-row">
              <div className="av-field">
                <label>Excerpt</label>
                <input
                  type="text"
                  name="excerpt"
                  value={form.excerpt}
                  onChange={handleField}
                />
              </div>
            </div>

            <div className="av-row">
              <div className="av-field">
                <label>Category</label>
                <input
                  type="text"
                  name="category"
                  value={form.category}
                  onChange={handleField}
                  placeholder="Optional (e.g. Workshop, Q&A)"
                />
              </div>

              <div className="av-field">
                <label>Duration</label>
                <input
                  type="text"
                  name="duration"
                  value={form.duration}
                  onChange={handleField}
                  placeholder="Optional (e.g. 47:50)"
                />
              </div>
            </div>

            <div className="av-row">
              <div className="av-field">
                <label>Published Date</label>
                <input
                  type="date"
                  name="publishedDate"
                  value={form.publishedDate}
                  onChange={handleField}
                />
              </div>

              <div className="av-field">
                <label>Status</label>
                <select name="status" value={form.status} onChange={handleField}>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>

            <label className="av-checkbox">
              <input
                type="checkbox"
                name="isFeatured"
                checked={form.isFeatured}
                onChange={handleCheckbox}
              />
              Featured video
            </label>
          </section>

          {/* TAGS */}
          <section className="av-section">
            <h2 className="av-section-title">Tags</h2>

            <div className="av-field-tags">
              <label>Tags</label>
              <div className="av-tags-container">
                {form.tags.map((t) => (
                  <span key={t} className="av-tag">
                    {t}
                    <button
                      type="button"
                      className="av-tag-remove"
                      onClick={() => removeTag(t)}
                    >
                      ✕
                    </button>
                  </span>
                ))}

                <input
                  id="tag-input"
                  className="av-tag-input"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKey}
                  onBlur={commitTagInput}
                  placeholder={
                    form.tags.length === 0 ? "Type and press Enter…" : ""
                  }
                />
              </div>
            </div>
          </section>

          {/* YOUTUBE LINK */}
          <section className="av-section">
            <h2 className="av-section-title">Video Source</h2>

            <div className="av-field">
              <label>YouTube Link</label>
              <input
                type="url"
                name="youtubeUrl"
                value={form.youtubeUrl}
                onChange={handleField}
                required
              />
              <p className="av-hint">
                Only this field is required. We auto-extract the video ID, thumbnail, and title where available.
              </p>
            </div>

            <div className="av-field">
              <label>Thumbnail URL</label>
              <input
                type="url"
                name="thumbnailUrl"
                value={form.thumbnailUrl}
                onChange={handleField}
                placeholder={autoThumb || "Optional override"}
              />
              {youtubeId && !form.thumbnailUrl && (
                <p className="av-hint">Auto thumbnail: {autoThumb}</p>
              )}
            </div>
          </section>

          {/* SAVE ERRORS */}
          {saveError && <p className="av-error">{saveError}</p>}

          {/* ACTIONS */}
          <div className="av-actions">
            <button className="av-button av-button-primary" disabled={saving}>
              {saving ? "Saving…" : "Save Video"}
            </button>

            <button
              type="button"
              className="av-button av-button-outline"
              onClick={cancelForm}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </form>

        {/* DELETE MODAL */}
        {deleteTarget && (
          <DeleteModal
            item={deleteTarget}
            deleting={deleting}
            error={deleteError}
            onCancel={() => setDeleteTarget(null)}
            onConfirm={confirmDelete}
          />
        )}
      </div>
    );
  }

  /* -------------------------------------------------------
     LIST MODE
  ------------------------------------------------------- */
  return (
    <div className="admin-events">
      <h1>Videos</h1>

      <div className="av-toolbar">
        <input
          className="av-search"
          placeholder="Search videos..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button className="av-button av-button-primary" onClick={startAdd}>
          Add Video
        </button>
      </div>

      {filtered.length === 0 ? (
        <p className="av-hint">No videos found.</p>
      ) : (
        <div className="av-list">
          {filtered.map((v) => (
            <div key={v.id} className="av-card">
              <div className="av-card-main">
                <strong>{v.title}</strong>
                <span className={`av-pill av-pill-${v.status}`}>
                  {v.status}
                </span>
                {v.isFeatured && (
                  <span className="av-pill av-pill-featured">Featured</span>
                )}
                <p className="av-card-meta">
                  {v.category ? "Category: " + v.category : "Category: General"}
                  {v.publishedDate && " • " + v.publishedDate}
                  {v.duration && " • " + v.duration}
                  {(v.tags || []).length > 0 && " • Tags: " + (v.tags || []).join(", ")}
                </p>
              </div>

              <div className="av-card-actions">
                <button
                  className="av-button av-button-outline"
                  onClick={() => startEdit(v)}
                >
                  Edit
                </button>

                <button
                  className="av-button av-button-danger-soft"
                  onClick={() => setDeleteTarget(v)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* DELETE MODAL */}
      {deleteTarget && (
        <DeleteModal
          item={deleteTarget}
          deleting={deleting}
          error={deleteError}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}

/* #######################################################
   DELETE MODAL (REUSABLE)
####################################################### */
function DeleteModal({ item, deleting, error, onCancel, onConfirm }) {
  return (
    <div className="av-modal-overlay">
      <div className="av-modal">
        <h2>Delete Video</h2>

        <p>
          Are you sure you want to delete{" "}
          <strong>{item.title}</strong>?
        </p>

        {error && <p className="av-error">{error}</p>}

        <div className="av-modal-actions">
          <button className="av-button av-button-outline" onClick={onCancel}>
            Cancel
          </button>

          <button
            className="av-button av-button-danger"
            disabled={deleting}
            onClick={onConfirm}
          >
            {deleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}
